import React, { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Add, DashboardCustomize, Save } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import {
  dashboardDataSourcesQueryClient,
  dashboardQueryClient,
  dashboardVariableSourcesQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import Header from '../../components/header';
import { Responsive, WidthProvider } from 'react-grid-layout';
import {
  DashboardDto,
  DataSourceSettingsDto,
  VariableDto,
  WidgetDto,
  WidgetSettingsDto,
} from '@thingslog/repositories';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';
import {
  DashboardConfirmationDialog,
  DashboardHeader,
  DashboardMobileNavigation,
  DashboardSelectionSidebar,
  DashboardTours,
  DashboardWidgetSidebar,
  WidgetVisualizer,
} from './components';
import {
  useDashboardOperations,
  useDashboardLayoutOperations,
  useDashboardMobileView,
  useDashboardSelection,
  useDashboardHeaderTourAutoStart,
  useDashboardTours,
  useDashboardWidgetSidebarActions,
  useDashboardVariableOperations,
  useDashboardWidgetOperations,
} from './hooks';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

interface Props {}

const ResponsiveGridLayout = WidthProvider(Responsive);
const DESKTOP_ROW_HEIGHT = 300;
const MOBILE_ROW_HEIGHT = 100;

interface DashboardGridWidgetProps extends React.HTMLAttributes<HTMLDivElement> {
  companyId: number | null;
  dashboard: DashboardDto;
  dashboardVariables: VariableDto[];
  dataSources: DataSourceSettingsDto[];
  onEditDashboardWidget: (widget: WidgetDto) => void;
  onEditDashboardWidgetTimeframe: (widget: WidgetDto) => void;
  widget: WidgetDto;
}

const DashboardGridWidget = React.memo(
  React.forwardRef<HTMLDivElement, DashboardGridWidgetProps>(
    (props: DashboardGridWidgetProps, ref: React.ForwardedRef<HTMLDivElement>): JSX.Element => {
      const {
        children,
        className,
        companyId,
        dashboard,
        dashboardVariables,
        dataSources,
        onEditDashboardWidget,
        onEditDashboardWidgetTimeframe,
        widget,
        ...rest
      } = props;

      const handleEdit = useCallback(
        (_widgetSettings: WidgetSettingsDto): void => {
          onEditDashboardWidget(widget);
        },
        [onEditDashboardWidget, widget]
      );

      const handleEditTimeframe = useCallback(
        (_widgetSettings: WidgetSettingsDto): void => {
          onEditDashboardWidgetTimeframe(widget);
        },
        [onEditDashboardWidgetTimeframe, widget]
      );

      return (
        <div
          ref={ref}
          className={`bg-white rounded-lg shadow relative ${className ?? ''}`}
          {...rest}
        >
          <div className="p-0 w-full h-full overflow-hidden">
            <WidgetVisualizer
              widgetSettings={widget.settings}
              dataSources={dataSources}
              dashboard={dashboard}
              dashboardVariables={dashboardVariables}
              fromDate={null}
              toDate={null}
              companyId={companyId}
              onEdit={handleEdit}
              onEditTimeframe={handleEditTimeframe}
            />
          </div>
          {children}
        </div>
      );
    }
  )
);

DashboardGridWidget.displayName = 'DashboardGridWidget';

export const DashboardPage: FC<Props> = (props: Props) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { useDashboard, useDashboards } = dashboardQueryClient;
  const { useDataSources } = dashboardDataSourcesQueryClient;
  const { useVariableSources } = dashboardVariableSourcesQueryClient;
  const companyId = useDetermineCompanyId();

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const dashboardsQuery = useDashboards(companyId ?? undefined);

  const variableSourcesQuery = useVariableSources({
    refetchOnWindowFocus: false,
  });

  const dataSourcesQuery = useDataSources({
    refetchOnWindowFocus: false,
  });

  const dashboardQuery = useDashboard(id!, companyId ?? undefined, {
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const dashboard = dashboardQuery.data;
  const dashboards = dashboardsQuery.data;
  const dataSources = dataSourcesQuery.data;
  const variableSources = variableSourcesQuery.data;
  const isDashboardLoaded = !!dashboard;

  const { isDashboardSelectionSidebarOpen, handleDashboardSelectionToggle, handleSelectDashboard } =
    useDashboardSelection(companyId);

  const {
    dashboardVariablesLocal,
    handleTempSaveVariable,
    handlePersistVariable,
    handlePersistNewVariable,
    handleDeleteVariable,
    isPersistingVariable,
    isPersistingNewVariable,
    isDeletingVariable,
  } = useDashboardVariableOperations(id!, companyId, variableSources ?? []);

  const {
    dashboardWidgetSideWidgets,
    handleCreateVariablesWidget,
    handleSetVariablesWidget,
    handleCreateAddVariableWidget,
    handleCreateEditVariableWidget,
    handleCreateAddDashboardWidget,
    handleCreateEditDashboardWidget,
    handleCreateEditDashboardTimeframeWidget,
    handleCreateDashboardWidgetsWidget,
    handleSetDashboardWidgetsWidget,
    handleCreateAddDashboardWidgetWidget,
    handleCreateEditDashboardWidgetWidget,
    handleCreateEditDashboardWidgetTimeframeWidget,
    handleCloseSidebarWidget,
    handleCloseAllSidebarWidgets,
  } = useDashboardWidgetSidebarActions();

  const {
    handlePersistWidget,
    handlePersistNewWidget,
    handleDeleteWidget,
    isPersistingWidget,
    isPersistingNewWidget,
    isDeletingWidget,
  } = useDashboardWidgetOperations(id!, companyId);

  const {
    handlePersistDashboard,
    handlePersistNewDashboard,
    isPersistingDashboard,
    isPersistingNewDashboard,
  } = useDashboardOperations(companyId, dataSources ?? []);

  const {
    layouts,
    isDashboardLayoutEditable,
    isDashboardLayoutEditing,
    isPersistingDashboardLayout,
    isSaveDashboardLayoutDialogOpen,
    handleEditDashboardLayout,
    handleRequestSaveDashboardLayout,
    handleCloseSaveDashboardLayoutDialog,
    handleSaveDashboardLayout,
    handleLayoutChange,
  } = useDashboardLayoutOperations({
    dashboardId: id!,
    companyId,
    widgets: dashboard?.widgets ?? null,
    isEditable: dashboard?.isEditable ?? false,
  });

  const handleHeaderToggle = (): void => {
    setIsHeaderVisible((prev: boolean) => !prev);
  };

  const {
    isDashboardTourRunning,
    dashboardTourSteps,
    handleDashboardTourEvent,
    handleStartHeaderTour,
    handleStartDashboardSelectionTour,
    handleStartSidebarWidgetTour,
  } = useDashboardTours();

  const isDashboardWidgetSidebarOpen = useMemo(
    () => dashboardWidgetSideWidgets.length > 0,
    [dashboardWidgetSideWidgets]
  );

  const hasDashboardWidgets = useMemo(
    () => !!dashboard && dashboard.widgets.length > 0,
    [dashboard]
  );

  const {
    isMobileDashboardView,
    activeMobileNavigationValue,
    handleMobileDashboardAction,
    handleMobileLayoutAction,
    handleMobileWidgetsAction,
    handleMobileVariablesAction,
    handleMobileDashboardsAction,
    isMobileRootWidget,
  } = useDashboardMobileView({
    dashboardWidgetSideWidgets,
    isDashboardSelectionSidebarOpen,
    isDashboardWidgetSidebarOpen,
    isDashboardLayoutEditing,
    handleDashboardSelectionToggle,
    handleCloseAllSidebarWidgets,
    handleSetDashboardWidgetsWidget,
    handleSetVariablesWidget,
    handleEditDashboardLayout,
    handleRequestSaveDashboardLayout,
  });

  useDashboardHeaderTourAutoStart({
    isDashboardLoaded,
    isMobileDashboardView,
    onStartHeaderTour: handleStartHeaderTour,
  });

  return (
    <Header fullHeightContent isHeaderVisible={isHeaderVisible}>
      <DashboardTours
        run={isDashboardTourRunning}
        steps={dashboardTourSteps}
        onEvent={handleDashboardTourEvent}
      />
      <div className="flex h-full w-full flex-col overflow-auto pb-14 md:pb-0">
        {!isMobileDashboardView && (
          <DashboardHeader
            dashboardName={dashboard?.name || t('dashboard_loading', 'Loading...')}
            dashboardPeriod={dashboard?.period}
            isDashboardSelectionOpen={isDashboardSelectionSidebarOpen}
            isDashboardEditable={isDashboardLayoutEditable}
            isEditingDashboard={isDashboardLayoutEditing}
            isSavingDashboardLayout={isPersistingDashboardLayout}
            isHeaderVisible={isHeaderVisible}
            onDashboardSelectionToggle={handleDashboardSelectionToggle}
            onHeaderToggle={handleHeaderToggle}
            onEditDashboard={handleCreateEditDashboardWidget}
            onEditDashboardTimeframe={handleCreateEditDashboardTimeframeWidget}
            onAddWidget={handleCreateAddDashboardWidgetWidget}
            onViewVariablesToggle={handleCreateVariablesWidget}
            onEditDashboardLayout={handleEditDashboardLayout}
            onSaveDashboardLayout={handleRequestSaveDashboardLayout}
            onStartTour={handleStartHeaderTour}
          />
        )}
        <main className="flex-1 flex flex-row overflow-hidden">
          <DashboardSelectionSidebar
            open={isDashboardSelectionSidebarOpen}
            dashboards={dashboards ?? []}
            currentDashboardId={id}
            onAddDashboard={handleCreateAddDashboardWidget}
            onEditCurrentDashboard={handleCreateEditDashboardWidget}
            onSelectDashboard={handleSelectDashboard}
            onStartTour={handleStartDashboardSelectionTour}
            className="shrink-0"
          />
          <section className="flex-1 min-w-0 overflow-auto">
            <div className="sticky top-4 z-10 h-px w-full" data-dashboard-tour="dashboard-canvas" />
            {isMobileDashboardView && isDashboardLayoutEditable && (
              <div className="sticky top-3 z-20 flex justify-end px-3">
                <Button
                  variant={isDashboardLayoutEditing ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                  disableElevation
                  startIcon={
                    isDashboardLayoutEditing ? (
                      <Save fontSize="small" />
                    ) : (
                      <DashboardCustomize fontSize="small" />
                    )
                  }
                  disabled={isPersistingDashboardLayout}
                  className="bg-white shadow-sm"
                  onClick={handleMobileLayoutAction}
                >
                  {isDashboardLayoutEditing
                    ? t('dashboard_save_layout', 'Save layout')
                    : t('dashboard_edit_layout', 'Edit layout')}
                </Button>
              </div>
            )}
            {dashboardQuery.isLoading && (
              <div className="flex min-h-full items-center justify-center gap-2 px-6 py-10 text-gray-600">
                <CircularProgress size={24} />
                <Typography variant="body2">{t('dashboard_loading', 'Loading...')}</Typography>
              </div>
            )}
            {dashboardQuery.isError && (
              <div className="px-6 py-6">
                <Alert severity="error">
                  {t(
                    'dashboard_load_error',
                    'Unable to load this dashboard. Refresh and try again.'
                  )}
                </Alert>
              </div>
            )}
            {!dashboardQuery.isLoading && dashboard && dataSourcesQuery.isLoading && (
              <div className="flex min-h-full items-center justify-center gap-2 px-6 py-10 text-gray-600">
                <CircularProgress size={24} />
                <Typography variant="body2">
                  {t('dashboard_loading_data_sources', 'Loading data sources...')}
                </Typography>
              </div>
            )}
            {dataSourcesQuery.isError && (
              <div className="px-6 py-6">
                <Alert severity="error">
                  {t(
                    'dashboard_data_sources_load_error',
                    'Unable to load dashboard data sources. Widgets cannot be rendered yet.'
                  )}
                </Alert>
              </div>
            )}
            {dashboard && !hasDashboardWidgets && (
              <div className="flex min-h-full items-center justify-center px-6 py-10">
                <div className="flex max-w-md flex-col items-center gap-3 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-cyan-100 bg-cyan-50 text-cyan-700">
                    <Add />
                  </div>
                  <Typography variant="h6">
                    {t('dashboard_empty_title', 'Create your first widget')}
                  </Typography>
                  <Typography variant="body2" className="text-gray-600">
                    {t(
                      'dashboard_empty_description',
                      'This dashboard does not have any widgets yet. Add a widget to start displaying data here.'
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<Add />}
                    onClick={handleCreateAddDashboardWidgetWidget}
                  >
                    {t('dashboard_add_widget', 'Add widget')}
                  </Button>
                </div>
              </div>
            )}
            {layouts && dashboard && dataSources && hasDashboardWidgets && (
              <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                breakpoints={{ desktop: dashboard.layoutSwitchBreakpointPx, mobile: 0 }}
                cols={{ desktop: dashboard.colsDesktop, mobile: dashboard.colsMobile }}
                rowHeight={isMobileDashboardView ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT}
                onLayoutChange={handleLayoutChange}
                isResizable={isDashboardLayoutEditing}
                isDraggable={isDashboardLayoutEditing}
              >
                {dashboard.widgets.map((widget: WidgetDto): React.ReactNode => {
                  return (
                    <DashboardGridWidget
                      key={widget.id}
                      widget={widget}
                      dataSources={dataSources}
                      dashboard={dashboard}
                      dashboardVariables={dashboardVariablesLocal}
                      companyId={companyId}
                      onEditDashboardWidget={handleCreateEditDashboardWidgetWidget}
                      onEditDashboardWidgetTimeframe={
                        handleCreateEditDashboardWidgetTimeframeWidget
                      }
                    />
                  );
                })}
              </ResponsiveGridLayout>
            )}
          </section>
          {dashboard && (
            <DashboardWidgetSidebar
              dashboard={dashboard}
              dashboardWidgets={dashboard.widgets}
              dataSources={dataSourcesQuery.data ?? []}
              variables={dashboardVariablesLocal}
              variableSources={variableSourcesQuery.data ?? []}
              companyId={companyId}
              open={isDashboardWidgetSidebarOpen}
              widgets={dashboardWidgetSideWidgets}
              onAddVariable={handleCreateAddVariableWidget}
              onEditVariable={handleCreateEditVariableWidget}
              onDeleteVariable={handleDeleteVariable}
              onPersistVariable={handlePersistVariable}
              onTempSaveVariable={handleTempSaveVariable}
              onPersistNewVariable={handlePersistNewVariable}
              isPersistingVariable={isPersistingVariable}
              isPersistingNewVariable={isPersistingNewVariable}
              isDeletingVariable={isDeletingVariable}
              onPersistDashboard={handlePersistDashboard}
              onPersistNewDashboard={handlePersistNewDashboard}
              isPersistingDashboard={isPersistingDashboard}
              isPersistingNewDashboard={isPersistingNewDashboard}
              onAddDashboardWidget={handleCreateAddDashboardWidgetWidget}
              onEditDashboardWidget={handleCreateEditDashboardWidgetWidget}
              onPersistWidget={handlePersistWidget}
              onPersistNewWidget={handlePersistNewWidget}
              onDeleteWidget={handleDeleteWidget}
              isPersistingWidget={isPersistingWidget}
              isPersistingNewWidget={isPersistingNewWidget}
              isDeletingWidget={isDeletingWidget}
              onCloseWidget={handleCloseSidebarWidget}
              onStartWidgetTour={handleStartSidebarWidgetTour}
              isMobileRootWidget={isMobileRootWidget}
              className="shrink-0"
            />
          )}
        </main>
      </div>

      {isMobileDashboardView && (
        <DashboardMobileNavigation
          value={activeMobileNavigationValue}
          onDashboardAction={handleMobileDashboardAction}
          onWidgetsAction={handleMobileWidgetsAction}
          onVariablesAction={handleMobileVariablesAction}
          onDashboardsAction={handleMobileDashboardsAction}
        />
      )}

      <DashboardConfirmationDialog
        open={isSaveDashboardLayoutDialogOpen}
        title={t('dashboard_confirm_changes_title', 'Confirm Changes')}
        cancelLabel={t('close_btn', 'Close')}
        confirmLabel={t('save_btn', 'Save')}
        confirmColor="primary"
        disabled={isPersistingDashboardLayout}
        onClose={handleCloseSaveDashboardLayoutDialog}
        onConfirm={handleSaveDashboardLayout}
      >
        {t(
          'dashboard_save_layout_confirmation',
          'Are you sure you want to save your changes? All modifications will be applied to the dashboard.'
        )}
      </DashboardConfirmationDialog>
    </Header>
  );
};
